import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const token = pathToken || searchParams.get('token');
  const emailFromUrl = searchParams.get('email') || '';
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const checks = [
    { ok: newPassword.length >= 8, label: 'At least 8 characters' },
    { ok: /[A-Z]/.test(newPassword), label: 'Uppercase letter (A-Z)' },
    { ok: /[a-z]/.test(newPassword), label: 'Lowercase letter (a-z)' },
    { ok: /[0-9]/.test(newPassword), label: 'Number (0-9)' },
    { ok: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(newPassword), label: 'Special character' },
  ];
  const allOk = checks.every(c => c.ok);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  const handleReset = async () => {
    if (!allOk) { toast.error('Password does not meet complexity requirements'); return; }
    if (!passwordsMatch) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/reset-password/${token}`, {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success(res.data.message);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password reset failed. The link may have expired.');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-4">
      <div className="w-full max-w-md bg-card rounded-2xl shadow-2xl p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
            <Lock className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#1e3a5f' }}>Reset Your Password</h1>
          <p className="text-sm text-muted-foreground mt-1">Eyvo eProcurement</p>
        </div>

        {done ? (
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-2">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-green-700">Password Reset Successfully</h2>
            <p className="text-sm text-muted-foreground">You can now log in with your new password.</p>
            <Button onClick={() => navigate('/login')} className="w-full">Go to Login</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* New Password */}
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                autoComplete="new-password"
              />
              {newPassword && (
                <div className="space-y-1 mt-2">
                  {checks.map(c => (
                    <div key={c.label} className={`flex items-center gap-2 text-xs ${c.ok ? 'text-green-600' : 'text-red-400'}`}>
                      {c.ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      {c.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
              {confirmPassword && (
                <p className={`text-xs mt-1 ${passwordsMatch ? 'text-green-600' : 'text-red-400'}`}>
                  {passwordsMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
                </p>
              )}
            </div>

            {/* Submit */}
            <Button
              onClick={handleReset}
              disabled={saving || !allOk || !passwordsMatch}
              className="w-full"
            >
              {saving ? 'Resetting...' : 'Reset Password'}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              This link expires after 24 hours.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
